// Sektör paketi uçları (spec §4, routes/kpi-packages.ts — GET kpi.view, yazma kpi.manage) ve paket
// AI önerisi. Hata kodları KPI_PACKAGE_ERROR_CODES (ApiError.code) → metin lib/kpi-packages-copy.ts.
// Her yanıt shared Zod şemasıyla ayrıştırılır (TODO-KPI-143): sunucu zarfı değişirse ya da istemci
// yanlış şekil varsayarsa hata derleme/çalıştırmada değil ilk okumada ZodError olarak görünür —
// "çıplak dizi sanıldı" sınıfı bir daha sessiz kalmaz. Sözleşme backend route testinde aynı
// şemalarla kilitlidir (kpi-packages-routes.test.ts).

import {
  KpiPackageDetailResponseSchema,
  KpiPackageFromSetResultSchema,
  KpiPackageItemsResultSchema,
  KpiPackageListResponseSchema,
  KpiPackageMembershipResponseSchema,
  KpiPackageSchema,
  KpiPackageSuggestResponseSchema,
  KpiPromotePreviewSchema,
  KpiPromoteResultSchema,
  KpiSetBulkAddResultSchema,
  type KpiPackage,
  type KpiPackageCreate,
  type KpiPackageDetailResponse,
  type KpiPackageFromSetBody,
  type KpiPackageFromSetResult,
  type KpiPackageItemsBody,
  type KpiPackageItemsResult,
  type KpiPackageSuggestRequest,
  type KpiPackageSuggestResponse,
  type KpiPackageUpdate,
  type KpiPromoteBody,
  type KpiPromotePreview,
  type KpiPromoteResult,
  type KpiSetBulkAddBody,
  type KpiSetBulkAddResult,
} from "@mahir/shared";
import { api } from "./client";

export type { KpiPackageFromSetResult, KpiPromoteResult };
/** `GET /kpi/packages/:id` gövdesi. */
export type KpiPackageDetail = KpiPackageDetailResponse;

export interface KpiPackageListParams {
  sectorCode?: string;
  status?: "active" | "archived";
  /** Müşteri sektör ipucu: bu sektöre eşleşen etkin paketleri döner. */
  suggestFor?: string;
}

export async function listKpiPackages(params: KpiPackageListParams = {}): Promise<KpiPackage[]> {
  const q = new URLSearchParams();
  if (params.sectorCode) q.set("sectorCode", params.sectorCode);
  if (params.status) q.set("status", params.status);
  if (params.suggestFor) q.set("suggestFor", params.suggestFor);
  const qs = q.toString();
  const raw = await api<unknown>(`/kpi/packages${qs ? `?${qs}` : ""}`);
  return KpiPackageListResponseSchema.parse(raw).packages;
}

/** metricId → onu içeren etkin paket kimlikleri (Katalog paket kolonu / süzgeç). */
export async function listKpiPackageMembership(): Promise<Record<string, string[]>> {
  const raw = await api<unknown>("/kpi/packages/membership");
  return KpiPackageMembershipResponseSchema.parse(raw).membership;
}

export async function getKpiPackage(id: string): Promise<KpiPackageDetail> {
  return KpiPackageDetailResponseSchema.parse(await api<unknown>(`/kpi/packages/${id}`));
}

export async function createKpiPackage(body: KpiPackageCreate): Promise<KpiPackage> {
  return KpiPackageSchema.parse(await api<unknown>("/kpi/packages", { method: "POST", body }));
}

export async function updateKpiPackage(id: string, patch: KpiPackageUpdate): Promise<KpiPackage> {
  return KpiPackageSchema.parse(
    await api<unknown>(`/kpi/packages/${id}`, { method: "PATCH", body: patch }),
  );
}

/** Tüm üye listesini değiştirir (taslak şeridi Kaydet); `expectedUpdatedAt` eskiyse KPI_PACKAGE_STALE. */
export async function putKpiPackageItems(
  id: string,
  body: KpiPackageItemsBody,
): Promise<KpiPackageItemsResult> {
  return KpiPackageItemsResultSchema.parse(
    await api<unknown>(`/kpi/packages/${id}/items`, { method: "PUT", body }),
  );
}

export async function addKpiPackageItems(
  id: string,
  metricIds: string[],
): Promise<KpiPackageItemsResult> {
  return KpiPackageItemsResultSchema.parse(
    await api<unknown>(`/kpi/packages/${id}/items`, { method: "POST", body: { metricIds } }),
  );
}

/** `POST /kpi/packages/from-set/:setId` — müşteriye özel göstergeler atlanır ve listelenir. */
export async function createKpiPackageFromSet(
  setId: string,
  body: KpiPackageFromSetBody,
): Promise<KpiPackageFromSetResult> {
  return KpiPackageFromSetResultSchema.parse(
    await api<unknown>(`/kpi/packages/from-set/${setId}`, { method: "POST", body }),
  );
}

/** Pakete ve/veya tek tek gösterge seçimini TEK tx'te sete ekler (atalar otomatik). */
export async function bulkAddKpiSetIndicators(
  setId: string,
  body: KpiSetBulkAddBody,
): Promise<KpiSetBulkAddResult> {
  return KpiSetBulkAddResultSchema.parse(
    await api<unknown>(`/kpi/sets/${setId}/indicators/bulk`, { method: "POST", body }),
  );
}

/** Müşteriye özel göstergeyi kataloğa yükseltir; `dryRun: true` yalnız önizler (yazmaz). */
export async function promoteKpiMetric(
  metricId: string,
  body: KpiPromoteBody & { dryRun: true },
): Promise<KpiPromotePreview>;
export async function promoteKpiMetric(
  metricId: string,
  body: KpiPromoteBody,
): Promise<KpiPromoteResult>;
export async function promoteKpiMetric(
  metricId: string,
  body: KpiPromoteBody,
): Promise<KpiPromoteResult | KpiPromotePreview> {
  const raw = await api<unknown>(`/kpi/metrics/${metricId}/promote`, { method: "POST", body });
  return body.dryRun === true
    ? KpiPromotePreviewSchema.parse(raw)
    : KpiPromoteResultSchema.parse(raw);
}

export async function suggestKpiPackage(
  id: string,
  body: KpiPackageSuggestRequest,
  signal?: AbortSignal,
): Promise<KpiPackageSuggestResponse> {
  const raw = await api<unknown>(`/kpi/packages/${id}/suggest`, {
    method: "POST",
    body,
    ...(signal ? { signal } : {}),
  });
  return KpiPackageSuggestResponseSchema.parse(raw);
}
